import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

/** Default duration of the ink-reveal wipe, in seconds. */
const INK_REVEAL_SECONDS = 0.9;

interface InkRevealProps {
  children: ReactNode;
  /**
   * Replays the wipe whenever this value changes (e.g. a new letter in
   * `LetterIntro`). Ignored under reduced motion, where the content is
   * always shown fully revealed with no animation at all.
   */
  revealKey?: string;
  /** Wipe duration in seconds; defaults to {@link INK_REVEAL_SECONDS}. */
  durationSeconds?: number;
  className?: string;
}

/**
 * InkReveal — a left-to-right clip-path wipe that lays its content down
 * like ink from a pen. The content is the real font-rendered glyph (always
 * correct by construction — no hand-authored strokes), so this is purely a
 * reveal, not a redraw. Not a gradient. Under reduced motion the key never
 * changes and the content renders fully revealed instantly.
 *
 * Never letter-space Devanagari content passed through here (ledger rule
 * 22) — the wipe clips glyphs, it must not reshape them.
 */
export function InkReveal({
  children,
  revealKey = "once",
  durationSeconds = INK_REVEAL_SECONDS,
  className = "",
}: InkRevealProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.span
      key={prefersReducedMotion ? "static" : revealKey}
      className={`inline-block ${className}`}
      initial={
        prefersReducedMotion
          ? { clipPath: "inset(0 0% 0 0)" }
          : { clipPath: "inset(0 100% 0 0)" }
      }
      animate={{ clipPath: "inset(0 0% 0 0)" }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: durationSeconds, ease: "easeOut" }
      }
    >
      {children}
    </motion.span>
  );
}
